import { gql } from '@apollo/client';
import { Button, message } from 'antd';
import Page from 'components/Page';
import StatusTag from 'components/StatusTag';
import Table from 'components/Table';
import { useMutation, useQuery } from 'hooks/apollo';
import React from 'react';
import { JobStatus } from 'shared/prisma/types';

const QUERY = gql`
  query {
    autoTaggings {
      id
      createdAt      
      createdBy {
        id
        email 
      }
      status
      question {
        id
        title
        survey {
          id
          title
          customer {
            id
            name
          }
        }
      }
    }
  }
`;

const RETRY_AUTO_TAGGING = gql`
  mutation($id: ID!) {
    retryAutoTagging(id: $id)
  }
`;

const AutoTaggings = () => {
  const { data, loading, refetch } = useQuery(QUERY);
  const [retryingId, setRetryingId] = React.useState(null);
  const [retryAutoTagging] = useMutation(RETRY_AUTO_TAGGING);

  const handleRetry = async (id) => {
    setRetryingId(id);
    try {
      await retryAutoTagging({ variables: { id } });
      await refetch();
      message.success('Auto tagging retried');
    } catch (e) {
      message.error(e.message);
    } finally {
      setRetryingId(null);
    }
  };

  const columns = [{
    title: 'Date',
    dataIndex: 'createdAt',
    type: 'date',
    defaultSortOrder: 'descend',
  }, {
    title: 'Account',
    dataIndex: ['question', 'survey', 'customer', 'name'],
  }, {
    title: 'Survey',
    dataIndex: ['question', 'survey', 'title'],
  }, {
    title: 'Question',
    dataIndex: ['question', 'title'],
  }, {
    title: 'Created by',
    dataIndex: ['createdBy', 'email'],
  }, {
    title: 'Status',
    dataIndex: 'status',
    render: (status) => <StatusTag status={status} />,
  }];

  const actions = (record) => {
    if ([JobStatus.Ongoing].includes(record.status)) {
      return null;
    }

    return (
      <Button
        loading={retryingId === record.id}
        onClick={() => handleRetry(record.id)}
      >
        Run
      </Button>
    );
  };

  return (
    <Page>
      <Page.Header title="AI tagging" />
      <Page.Content>
        <Table dataSource={data?.autoTaggings} loading={loading} columns={columns} actions={actions} />
      </Page.Content>
    </Page>
  );
};

export default AutoTaggings;
